import React, { useState } from "react";
import { Box, Flex } from "theme-ui";

import Span from "components/typography/Span";
import Subtext from "components/typography/Subtext";
import { ChevronIcon } from "components/svg";
import { themevals } from "theme/themevals";

/**
 * MASTER_PLAN.md §3.9 #11 (mockup 23) — composable disclosure block.
 * Renders one plan-supplied disclosure notice (or a future site
 * access / hazard / PO notes block). Severity drives the border and
 * icon color; content is arbitrary paragraph text.
 *
 * Two rendering modes:
 *   - collapsible (default) — for post-acknowledgment inline banners
 *     on view.tsx. Click chevron to collapse to one-line summary.
 *   - static — for the acknowledgment modal (no collapse UI wanted).
 *
 * Zero-block plans render nothing; the parent should not mount this
 * component when `disclosures.length === 0`.
 */
export type Disclosure = {
  type: "payment_terms" | "site_access" | "hazard" | "po_notes";
  header: string;
  body: string;
  severity: "info" | "warn" | "critical";
};

export default function DisclosureBlock({
  disclosure,
  collapsible = true,
  initialCollapsed = false,
}: {
  disclosure: Disclosure;
  collapsible?: boolean;
  initialCollapsed?: boolean;
}) {
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const cfg = severityConfig(disclosure.severity);

  return (
    <Box
      sx={{
        background: cfg.bg,
        border: "1px solid",
        borderColor: cfg.border,
        borderRadius: "8px",
        padding: "12px 16px",
        marginBottom: "10px",
      }}
    >
      <Flex sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Span
          fontFamily={"Poppins"}
          fontSize={"13px"}
          fontWeight={600}
          color={cfg.fg}
        >
          {disclosure.header}
        </Span>
        {collapsible && (
          <Box
            as="button"
            onClick={() => setCollapsed((c) => !c)}
            sx={{
              background: "transparent",
              border: "none",
              padding: "4px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
            aria-label={collapsed ? "Expand" : "Collapse"}
          >
            <ChevronIcon
              width="10px"
              fill={cfg.fg}
              transform={collapsed ? "rotate(-90deg)" : "rotate(0deg)"}
            />
          </Box>
        )}
      </Flex>
      {!collapsed && (
        <Subtext
          fontSize={"12px"}
          fontFamily={"Poppins"}
          margin={0}
          marginTop={"6px"}
          color={cfg.body}
          sx={{ whiteSpace: "pre-wrap" }}
        >
          {disclosure.body}
        </Subtext>
      )}
    </Box>
  );
}

function severityConfig(s: Disclosure["severity"]): {
  bg: string;
  border: string;
  fg: string;
  body: string;
} {
  switch (s) {
    case "critical":
      return {
        bg: "#FCEBEB",
        border: "#E1B7B7",
        fg: "#791F1F",
        body: "#5A1616",
      };
    case "warn":
      return {
        bg: "#FFF8E1",
        border: "#E7CC7A",
        fg: "#633806",
        body: "#4A2A05",
      };
    case "info":
    default:
      return {
        bg: "#E1F5EE",
        border: "#A5D3C4",
        fg: "#085041",
        body: themevals.colors.black,
      };
  }
}
